import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, AlertTriangle } from 'lucide-react';

export const SecurityPolicyPage: React.FC = () => {
  return (
    <DocsLayout
      title='Security Policy'
      description='How we protect your data and respond to security issues'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Security' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <div className='mb-8'>
          <Card className='border-green-500/50 bg-green-500/5'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Shield className='h-5 w-5 text-green-600' />
                <CardTitle className='text-lg text-green-700 dark:text-green-400'>
                  Our Security Commitment
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                Keeping your data safe is one of our top priorities. This page explains what we do
                to protect your information and what happens if there is ever a security problem.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20' id='how-we-protect'>
          <h2 className='text-3xl font-bold mb-6'>1. How We Protect Your Data</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.1 Encryption</h3>
            <p className='text-sm leading-6 mb-4'>
              We use encryption to protect your data in two ways:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                <strong>In transit:</strong> When data moves between your computer and our servers,
                it is encrypted so no one can read it along the way
              </li>
              <li className='text-sm leading-6'>
                <strong>At rest:</strong> When data is stored on our servers, it is also encrypted
              </li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.2 Access Controls</h3>
            <p className='text-sm leading-6 mb-4'>We limit who can access your data:</p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                Only authorized Kebi AI staff can access the systems that store your data
              </li>
              <li className='text-sm leading-6'>Access requires multiple forms of verification</li>
              <li className='text-sm leading-6'>We track who accesses what and when</li>
              <li className='text-sm leading-6'>
                Staff only get access to what they need for their job
              </li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.3 Infrastructure Security</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>Our servers are hosted in secure data centers</li>
              <li className='text-sm leading-6'>We use firewalls to block unauthorized access</li>
              <li className='text-sm leading-6'>
                We regularly update our systems to fix security issues
              </li>
              <li className='text-sm leading-6'>We monitor our systems for suspicious activity</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.4 Regular Testing</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>We regularly test our systems for weaknesses</li>
              <li className='text-sm leading-6'>We review our code for security issues</li>
              <li className='text-sm leading-6'>We stay up to date on new security threats</li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='your-security'>
          <h2 className='text-3xl font-bold mb-6'>2. Your Role in Security</h2>
          <p className='text-sm leading-6 mb-4'>
            Security is a shared responsibility. Here is how you can help:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Use strong passwords:</strong> Choose passwords that are hard to guess
            </li>
            <li className='text-sm leading-6'>
              <strong>Do not share your login:</strong> Your account is for you only
            </li>
            <li className='text-sm leading-6'>
              <strong>Log out when done:</strong> Especially on shared computers
            </li>
            <li className='text-sm leading-6'>
              <strong>Report suspicious activity:</strong> Let us know if something seems wrong
            </li>
            <li className='text-sm leading-6'>
              <strong>Keep your devices secure:</strong> Use up-to-date browsers and operating
              systems
            </li>
          </ul>
        </section>

        <section className='mb-20' id='security-issues'>
          <h2 className='text-3xl font-bold mb-6'>3. If There Is a Security Problem</h2>

          <div className='mb-6'>
            <Card className='border-amber-500/50 bg-amber-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <AlertTriangle className='h-5 w-5 text-amber-600' />
                  <CardTitle className='text-lg text-amber-700 dark:text-amber-400'>
                    Our Response Process
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p>
                  If we ever discover a security issue that might affect your data, here is what we
                  do:
                </p>
              </CardContent>
            </Card>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.1 Immediate Response</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>We work immediately to stop the problem</li>
              <li className='text-sm leading-6'>
                We figure out what happened and what is affected
              </li>
              <li className='text-sm leading-6'>We preserve evidence for investigation</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.2 Notification</h3>
            <p className='text-sm leading-6 mb-4'>
              If your data may have been affected, we will notify you:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                <strong>Within 72 hours</strong> of discovering the issue
              </li>
              <li className='text-sm leading-6'>By email to your account administrators</li>
              <li className='text-sm leading-6'>With clear information about what happened</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.3 What We Will Tell You</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>What happened (in plain language)</li>
              <li className='text-sm leading-6'>What data might be affected</li>
              <li className='text-sm leading-6'>What we are doing to fix it</li>
              <li className='text-sm leading-6'>What you should do (if anything)</li>
              <li className='text-sm leading-6'>How to contact us with questions</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.4 Recovery</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                We fix the problem and prevent it from happening again
              </li>
              <li className='text-sm leading-6'>We review what happened and learn from it</li>
              <li className='text-sm leading-6'>We update you when the issue is fully resolved</li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='reporting'>
          <h2 className='text-3xl font-bold mb-6'>4. Reporting Security Issues</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>4.1 If You Find a Problem</h3>
            <p className='text-sm leading-6 mb-4'>
              If you discover a security issue with Vehix, please let us know right away:
            </p>
            <div className='p-6 bg-muted/30 rounded-lg border border-border mb-4'>
              <p className='text-sm leading-6'>
                <strong>Security Team:</strong>{' '}
                <span className='text-primary'>security@kebi.ai</span>
              </p>
            </div>
            <p className='text-sm leading-6'>
              We take all reports seriously and will respond promptly.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>4.2 What to Include</h3>
            <p className='text-sm leading-6 mb-4'>
              When reporting a security issue, please tell us:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>What you found</li>
              <li className='text-sm leading-6'>How you found it</li>
              <li className='text-sm leading-6'>Any steps to reproduce the issue</li>
              <li className='text-sm leading-6'>Your contact information (optional but helpful)</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>4.3 If Your Account Seems Compromised</h3>
            <p className='text-sm leading-6 mb-4'>
              If you think someone else has accessed your account:
            </p>
            <ol className='list-decimal pl-6 space-y-2'>
              <li className='text-sm leading-6'>Change your password immediately</li>
              <li className='text-sm leading-6'>Contact us at security@kebi.ai</li>
              <li className='text-sm leading-6'>Review your recent account activity</li>
            </ol>
          </div>
        </section>

        <section className='mb-20' id='contact'>
          <h2 className='text-3xl font-bold mb-6'>5. Contact Us</h2>
          <p className='text-sm leading-6 mb-6'>For security questions or concerns:</p>

          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Security: <span className='text-primary'>security@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
              <br />
              Privacy: <span className='text-primary'>privacy@kebi.ai</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
